import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { queryClient, queryKeys } from '@/api/query-client';
import type { components } from '@/api/generated/schema';

export type ProjectResponse = components['schemas']['PixlideApplicationFeaturesVideoProjectsProjectResponse'];
export type TemplateResponse = components['schemas']['PixlideApplicationFeaturesTemplatesTemplateResponse'];
export type ProjectFieldInput = components['schemas']['PixlideApplicationFeaturesVideoProjectsProjectFieldInput'];

/** Projet vidéo, avec ses valeurs de champs actuelles — pour l'éditeur. */
export function useProject(projectId: string) {
  return useQuery({
    queryKey: queryKeys.project(projectId),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/projects/{projectId}', {
        params: { path: { projectId } },
      });

      return data!;
    },

    enabled: projectId !== '',
  });
}

/** Détail d'un template (schéma de champs), pour générer le formulaire dynamique. */
export function useTemplateDetail(templateId: string | null) {
  return useQuery({
    queryKey: queryKeys.template(templateId ?? ''),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/templates/{templateId}', {
        params: { path: { templateId: templateId! } },
      });

      return data!;
    },

    enabled: templateId !== null && templateId !== '',
  });
}

export function useCreateProjectFromTemplate() {
  return useMutation({
    mutationFn: async (params: { templateId: string; name: string; durationSeconds: number | null }) => {
      const { data } = await api.POST('/api/v1/projects/from-template', {
        body: {
          templateId: params.templateId,
          name: params.name,
          // null = aucune durée choisie : le rendu garde celle du template.
          durationSeconds: params.durationSeconds,
        },
      });

      return data!;
    },
  });
}

/**
 * Modifie le contenu et relance le rendu. La vidéo actuellement diffusée reste à l'antenne
 * pendant le nouveau rendu (voir `VideoProject.CompleteRender` côté backend) : aucun écran
 * ne devient noir parce qu'un prix a été corrigé.
 */
export function useUpdateProjectContent(projectId: string) {
  return useMutation({
    mutationFn: async (params: { fields: ProjectFieldInput[]; submitForRender: boolean; name?: string }) => {
      await api.PUT('/api/v1/projects/{projectId}/content', {
        params: { path: { projectId } },
        body: params,
      });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.project(projectId) });
    },
  });
}

/**
 * Aperçu — image fixe recalculée à la demande, débouncée côté appelant. Ne persiste rien :
 * aucun projet requis, aucun rendu vidéo déclenché (voir le commentaire de l'endpoint
 * côté backend).
 */
export function useProjectPreview() {
  return useMutation({
    mutationFn: async (params: { templateId: string; fields: ProjectFieldInput[] }) => {
      const { data } = await api.POST('/api/v1/projects/preview', {
        body: params,
      });

      return data!;
    },
  });
}

/**
 * Supprime un projet de « Mes vidéos ». Côté serveur c'est un archivage : le projet sort de
 * la bibliothèque mais reste en base, et l'API refuse si une diffusion l'utilise encore.
 */
export function useArchiveProject() {
  return useMutation({
    mutationFn: async (projectId: string) => {
      await api.DELETE('/api/v1/projects/{projectId}', { params: { path: { projectId } } });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects() });
    },
  });
}
